// @flow
import React, { PureComponent } from 'react';
import { AddButton } from 'common/components/core/button';
import _ from 'lodash';
import theme from './CloneableFieldList.scss';

type FieldProps = {
    className?: string,
    showDelete: boolean,
    onDeleteField: number => void,
    index: number
};

type AddButtonProps = {
    onClick: AddButtonProps => void,
    isDisabled: boolean
};

type Props = {
    renderField: FieldProps => React.ComponentType<any>,
    renderAddFieldButton: any => React.ComponentType<any>,
    onDelete?: (Array<string | number>) => void,
    accessor: Array<string>,
    indexOffset?: number,
    maxFieldCount?: number,
    initialFieldCount?: number,
    buttonText?: string,
    onAddClick?: () => void,
    disabled?: boolean
};

type State = {
    fieldCount: number
};

/* This component can be used when we need to render a single field
and we have an option to keep cloning the field as user desires */

class CloneableFieldList extends PureComponent<Props, State> {
    static defaultProps = {
        renderAddFieldButton: (props: AddButtonProps) => (
            <AddButton {...props} />
        ),
        indexOffset: 0,
        maxFieldCount: null,
        initialFieldCount: 1
    };

    constructor(props) {
        super(props);
        this.state = {
            fieldCount: props.initialFieldCount
        };
    }

    componentDidUpdate(prevProps) {
        const { initialFieldCount } = this.props;
        if (prevProps.initialFieldCount !== initialFieldCount) {
            this.setState({ fieldCount: initialFieldCount });
        }
    }

    addAnotherField = () => {
        this.setState(prevState => ({
            fieldCount: prevState.fieldCount + 1
        }));
        if (this.props.onAddClick) {
            this.props.onAddClick();
        }
    };

    deleteField = (index: number) => {
        const { onDelete, accessor = [] } = this.props;
        this.setState(prevState => ({
            fieldCount: prevState.fieldCount - 1
        }));
        onDelete?.([...accessor, index]);
    };

    getShowDeleteValue = () => {
        // Show delete button for all fields including first one if showDeleteForAll=true
        // Show delete button for first field only if there is more than one field added [default case]
        if (this.state.fieldCount > 1) {
            return true;
        }
        return false;
    };

    render() {
        const { fieldCount } = this.state;
        const { maxFieldCount, buttonText, disabled } = this.props;
        return (
            <div className={theme.fieldCollectionWrap}>
                {_.times(fieldCount, index =>
                    this.props.renderField({
                        ...this.props,
                        className: theme.repeatableField,
                        showDelete: this.getShowDeleteValue(),
                        onDeleteField: this.deleteField,
                        index: index + this.props.indexOffset
                    })
                )}
                {this.props.renderAddFieldButton({
                    onClick: this.addAnotherField,
                    isDisabled:
                        (maxFieldCount && fieldCount === maxFieldCount) ||
                        disabled,
                    buttonText
                })}
            </div>
        );
    }
}

export default CloneableFieldList;
